import { DataTable, LoadingBlock, Money, Pill, Section, StatCard, useAdminResource } from "./AdminUI";
import MarginPanel from "./MarginPanel";
import { formatDateTime } from "../../lib/datetime";

export default function RevenuePanel() {
  const { data, loading, error } = useAdminResource("/admin/revenue");
  if (loading) return <LoadingBlock label="Loading revenue" />;
  if (error) return <div className="brutal-card p-6 text-ink-accent text-sm" role="alert">{error}</div>;

  return (
    <>
      {data.mock_billing && (
        <div className="brutal-card p-4 mb-6 bg-state-warning-surface" role="alert">
          <div className="text-2xs uppercase tracking-widest text-state-warning-ink font-semibold mb-1">Mock billing is on</div>
          <p className="text-sm">
            <code className="font-mono-ui">STRIPE_API_KEY</code> is a placeholder, so checkout upgrades plans
            without charging. These figures are list price × current plan assignments, not money received.
          </p>
        </div>
      )}

      <Section id="revenue" title="// RECURRING REVENUE">
        <div className="grid md:grid-cols-3 gap-4">
          <StatCard label="MRR" value={<Money amount={data.mrr} />} hint="List price × active paid plans" testid="admin-mrr" />
          <StatCard label="ARR" value={<Money amount={data.arr} />} hint="MRR × 12" />
          <StatCard label="ARPU" value={<Money amount={data.arpu} />} hint="Per paying account" />
          <StatCard label="Paid accounts" value={data.paid_users} hint={`${data.free_users} on free`} />
          <StatCard label="Conversion" value={`${data.conversion_rate}%`} hint="Paid ÷ total accounts"
                    tone={data.conversion_rate > 0 ? "good" : "default"} />
          <StatCard label="MRR at risk" value={<Money amount={data.mrr_at_risk_suspended} />}
                    hint="Held by suspended accounts"
                    tone={data.mrr_at_risk_suspended > 0 ? "warn" : "default"} />
        </div>
      </Section>

      <Section id="plans" title="// REVENUE BY PLAN">
        <DataTable
          caption="Monthly recurring revenue broken down by plan"
          empty="No accounts yet."
          columns={[{ key: "plan", label: "Plan" }, { key: "users", label: "Accounts" },
                    { key: "unit", label: "Unit price" }, { key: "mrr", label: "MRR" },
                    { key: "share", label: "Share" }]}
          rows={data.by_plan}
          renderRow={(p) => (
            <tr key={p.plan} className="border-t border-rule">
              <td className="p-3">{p.name}</td>
              <td className="p-3 tabular-nums">{p.users}</td>
              <td className="p-3"><Money amount={p.unit_amount} /></td>
              <td className="p-3"><Money amount={p.mrr} /></td>
              <td className="p-3">
                {/* Width carries the value, but the number is always shown too --
                    the bar is not the only signal. */}
                <div className="flex items-center gap-2">
                  <div className="h-2 w-24 bg-surface-sunken brutal-border" aria-hidden="true">
                    <div className="h-full bg-brand"
                         style={{ width: `${data.mrr ? Math.round((p.mrr / data.mrr) * 100) : 0}%` }} />
                  </div>
                  <span className="text-2xs tabular-nums text-ink-muted">
                    {data.mrr ? Math.round((p.mrr / data.mrr) * 100) : 0}%
                  </span>
                </div>
              </td>
            </tr>
          )}
        />
      </Section>

      <MarginPanel />

      <Section id="txns" title="// RECENT TRANSACTIONS">
        <DataTable
          caption="Most recent payment transactions"
          empty="No transactions recorded."
          columns={[{ key: "date", label: "Date" }, { key: "plan", label: "Plan" },
                    { key: "amount", label: "Amount" }, { key: "status", label: "Status" },
                    { key: "session", label: "Session" }]}
          rows={data.recent_transactions}
          renderRow={(t) => (
            <tr key={t.txn_id || t.session_id} className="border-t border-rule">
              <td className="p-3 font-mono-ui text-xs whitespace-nowrap">{formatDateTime(t.created_at)}</td>
              <td className="p-3">{t.plan || "—"}</td>
              <td className="p-3"><Money amount={t.amount} /></td>
              <td className="p-3">
                <Pill tone={t.payment_status === "paid" ? "success" : t.payment_status === "failed" ? "danger" : "warning"}>
                  {t.payment_status || "unknown"}
                </Pill>
              </td>
              <td className="p-3 font-mono-ui text-xs text-ink-muted break-all">{t.session_id}</td>
            </tr>
          )}
        />
      </Section>
    </>
  );
}
